import React, { useState, useEffect } from 'react';
import { Plus, Eye, Edit, Trash2, X, Printer, Package } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import PurchaseOrderForm from './PurchaseOrderForm';
import PrintablePurchaseOrder from './PrintablePurchaseOrder';
import { formatCurrency } from '../../utils/currency';
import { useOrganization } from '../../hooks/useOrganization';

const PurchaseOrders = () => {
  const { organization } = useOrganization();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [_viewingOrder, _setViewingOrder] = useState(null);
  const [printingOrder, setPrintingOrder] = useState(null);
  const [receivingOrder, setReceivingOrder] = useState(null);
  const [receivingItems, setReceivingItems] = useState([]);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get('/api/purchase-orders');
      setOrders(response.data.purchaseOrders || response.data.orders || []);
    } catch (error) {
      toast.error('Failed to fetch purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingOrder(null);
    fetchOrders();
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setShowForm(true);
  };

  const handleDelete = async (order) => {
    if (!window.confirm(`Are you sure you want to delete purchase order ${order.orderNo}?`)) {
      return;
    }

    try {
      await axios.delete(`/api/purchase-orders/${order.id}`);
      toast.success('Purchase order deleted successfully');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to delete purchase order');
    }
  };

  const handleView = async (order) => {
    try {
      const response = await axios.get(`/api/purchase-orders/${order.id}`);
      const orderData = response.data;
      setPrintingOrder(orderData);
    } catch (error) {
      toast.error('Failed to load order details');
    }
  };

  const handleReceive = async (order) => {
    try {
      const response = await axios.get(`/api/purchase-orders/${order.id}`);
      const orderData = response.data;
      setReceivingOrder(orderData);
      setReceivingItems(orderData.items.map(item => {
        const alreadyReceived = item.receivedQuantity || 0;
        const remaining = item.quantity - alreadyReceived;
        return {
          ...item,
          receivedQty: remaining,
          alreadyReceived,
          remaining
        };
      }));
    } catch (error) {
      toast.error('Failed to load order details');
    }
  };

  const handleConfirmReceive = async () => {
    // Validate at least one item has received quantity
    const hasReceivedItems = receivingItems.some(item => item.receivedQty > 0);
    if (!hasReceivedItems) {
      toast.error('At least one item must have received quantity greater than 0');
      return;
    }
    
    // Validate no item exceeds remaining quantity
    const invalidItem = receivingItems.find(item => item.receivedQty > item.remaining);
    if (invalidItem) {
      toast.error(`Cannot receive more than remaining quantity for ${invalidItem.partName}. Remaining: ${invalidItem.remaining}`);
      return;
    }
    
    try {
      await axios.post(`/api/purchase-orders/${receivingOrder.id}/receive`, {
        items: receivingItems
      });
      toast.success('Items received successfully');
      setReceivingOrder(null);
      setReceivingItems([]);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to receive items');
    }
  };

  const _handlePrint = async (order) => {
    try {
      const response = await axios.get(`/api/purchase-orders/${order.id}`);
      const orderData = response.data;
      setPrintingOrder(orderData);
      
      const originalTitle = document.title;
      document.title = `PO-${orderData.orderNo}`;
      
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.print();
          setTimeout(() => {
            document.title = originalTitle;
          }, 1000);
        });
      });
    } catch (error) {
      toast.error('Failed to load order details for printing');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT': return 'bg-gray-100 text-gray-800';
      case 'SENT': return 'bg-yellow-100 text-yellow-800';
      case 'CONFIRMED': return 'bg-blue-100 text-blue-800';
      case 'PARTIAL_RECEIVED': return 'bg-orange-100 text-orange-800';
      case 'RECEIVED': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      case 'PAID': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Purchase Orders</h2>
        <button 
          onClick={() => setShowForm(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Purchase Order
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Requisition
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center">Loading...</td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    No purchase orders found
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {order.orderNo}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {order.requisition ? (
                        <span className="text-blue-600 font-medium">
                          {order.requisition.requestNo}
                        </span>
                      ) : (
                        <span className="text-gray-400">Manual</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {order.vendor?.name}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatCurrency(order.totalAmount || 0, organization?.currency_code)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleView(order)}
                          className="text-blue-600 hover:text-blue-800"
                          title="View Order"
                        >
                          👁️
                        </button>
                        {order.status === 'CONFIRMED' && (
                          <button 
                            onClick={() => handleReceive(order)}
                            className="text-purple-600 hover:text-purple-800"
                            title="Receive Items"
                          >
                            📦
                          </button>
                        )}
                        {order.status === 'PARTIAL_RECEIVED' && (
                          <button 
                            onClick={() => handleReceive(order)}
                            className="text-orange-600 hover:text-orange-800"
                            title="Receive Remaining Items"
                          >
                            📦
                          </button>
                        )}
                        {order.status !== 'PAID' && (
                          <button 
                            onClick={() => handleEdit(order)}
                            className="text-blue-600 hover:text-blue-800"
                            title={order.status === 'RECEIVED' ? 'Update Status' : 'Edit Order'}
                          >
                            ✏️
                          </button>
                        )}
                        {order.status !== 'PAID' && order.status !== 'RECEIVED' && (
                          <button 
                            onClick={() => handleDelete(order)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete Order"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Purchase Order Form Modal */}
      {showForm && (
        <PurchaseOrderForm
          onClose={() => {
            setShowForm(false);
            setEditingOrder(null);
          }}
          onSuccess={handleFormSuccess}
          editOrder={editingOrder}
        />
      )}



      {/* Print View */}
      {printingOrder && (
        <PrintablePurchaseOrder
          order={printingOrder}
          onClose={() => setPrintingOrder(null)}
        />
      )}

      {/* Receive Items Modal */}
      {receivingOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">Receive Items - {receivingOrder.orderNo}</h2>
              <button onClick={() => setReceivingOrder(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600">Vendor: <span className="font-medium">{receivingOrder.vendor?.name}</span></p>
              </div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Part</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Ordered</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Already Received</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Remaining</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Receive Now</th>
                  </tr>
                </thead>
                <tbody>
                  {receivingItems.map((item, index) => (
                    <tr key={index} className={`border-b ${
                      item.remaining === 0 ? 'bg-green-50' : ''
                    }`}>
                      <td className="px-4 py-3 text-sm">
                        {item.partName}
                        {item.remaining === 0 && (
                          <span className="ml-2 text-xs text-green-600 font-medium">✓ Fully Received</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">{item.quantity}</td>
                      <td className="px-4 py-3 text-sm font-medium text-green-600">{item.alreadyReceived}</td>
                      <td className="px-4 py-3 text-sm font-medium text-blue-600">{item.remaining}</td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          value={item.receivedQty}
                          onChange={(e) => {
                            const updated = [...receivingItems];
                            const value = parseInt(e.target.value) || 0;
                            updated[index].receivedQty = value;
                            setReceivingItems(updated);
                          }}
                          min="0"
                          max={item.remaining}
                          disabled={item.remaining === 0}
                          className={`w-24 px-2 py-1 border rounded ${
                            item.receivedQty > item.remaining ? 'border-red-500 bg-red-50' : ''
                          } ${
                            item.remaining === 0 ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                        />
                        {item.receivedQty > item.remaining && item.remaining > 0 && (
                          <p className="text-xs text-red-600 mt-1">Exceeds remaining qty</p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setReceivingOrder(null)}
                  className="px-4 py-2 border rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmReceive}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Confirm Receipt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrders;